/**
 * Grenade flight and blast — the shared half. Not a bullet: slow, bouncing,
 * damage once at a fixed moment, so it gets its own tiny simulation run on both
 * ends (the server authoritatively, the client from `grenadeThrown`); where the
 * arcs disagree the explosion's `pos` is the server's word. Pure: no RNG, no
 * time source.
 */

import type { Vec3 } from "../math/vec3.js";
import type { CollisionWorld } from "./collision.js";
import type { HitPose } from "./hitbox.js";
import { GRAVITY } from "../constants.js";

/** Leaves the hand at this many metres per second. A firm lob, not a rifle. */
export const THROW_SPEED_MPS = 16;
/** The hand is a little in front of and below the eye. */
export const THROW_ORIGIN_FWD_M = 0.4;
export const THROW_ORIGIN_DOWN_M = 0.15;
/** Extra upward pitch folded into the throw so an eye-level aim lobs. */
export const THROW_PITCH_UP_RAD = 0.17;

/** Fraction of the normal velocity kept on a bounce. Dead-ish, like a steel can. */
export const BOUNCE_RESTITUTION = 0.35;
/** Fraction of the tangential velocity kept on a bounce. */
export const BOUNCE_FRICTION = 0.7;
/** Below this speed a grenade lies still. Metres per second. */
export const REST_SPEED_MPS = 0.3;
/** Grenade radius for contact, metres. Small, so it rolls into corners. */
export const GRENADE_RADIUS_M = 0.04;
/** A grenade that somehow leaves the world is retired rather than chased. */
export const MAX_FALL_Y = -500;

export interface GrenadeState {
  id: string;
  ownerId: string;
  defId: string;
  pos: Vec3;
  vel: Vec3;
  /** Absolute sim tick it goes off. Set at ARMING, not at the throw. */
  explodeTick: number;
  /** True once it has come to rest; the step is then a no-op. */
  atRest: boolean;
}

/**
 * Advances one grenade by `dt` seconds: symplectic Euler under gameplay
 * `GRAVITY` (a grenade falling slower than its thrower would look wrong), a
 * segment raycast, and a reflection with restitution and friction on a hit; a
 * handful of bounces per step at most. Mutates `s` in place.
 */
export function stepGrenade(s: GrenadeState, world: CollisionWorld, dt: number): GrenadeState {
  if (s.atRest) return s;
  s.vel.y -= GRAVITY * dt;

  let remaining = dt;
  for (let bounce = 0; bounce < 4 && remaining > 0; bounce++) {
    const vx = s.vel.x;
    const vy = s.vel.y;
    const vz = s.vel.z;
    const speed = Math.sqrt(vx * vx + vy * vy + vz * vz);
    if (speed < 1e-6) break;
    const dist = speed * remaining;
    const dir = { x: vx / speed, y: vy / speed, z: vz / speed };
    const hit = world.raycast(s.pos, dir, dist + GRENADE_RADIUS_M);
    if (hit === null) {
      s.pos.x += vx * remaining;
      s.pos.y += vy * remaining;
      s.pos.z += vz * remaining;
      remaining = 0;
      break;
    }
    // Walk up to the contact, keeping the radius off the surface.
    const travel = Math.max(0, hit.dist - GRENADE_RADIUS_M);
    s.pos.x += dir.x * travel;
    s.pos.y += dir.y * travel;
    s.pos.z += dir.z * travel;
    remaining -= travel / speed;

    // Split the velocity about the normal: reflect and damp the normal part,
    // rub off some of the tangential part.
    const n = hit.normal;
    const vn = vx * n.x + vy * n.y + vz * n.z;
    const tx = vx - vn * n.x;
    const ty = vy - vn * n.y;
    const tz = vz - vn * n.z;
    s.vel.x = tx * BOUNCE_FRICTION - vn * BOUNCE_RESTITUTION * n.x;
    s.vel.y = ty * BOUNCE_FRICTION - vn * BOUNCE_RESTITUTION * n.y;
    s.vel.z = tz * BOUNCE_FRICTION - vn * BOUNCE_RESTITUTION * n.z;

    const after = Math.sqrt(s.vel.x * s.vel.x + s.vel.y * s.vel.y + s.vel.z * s.vel.z);
    // Resting on something that faces up: stop. Resting against a wall would
    // keep falling under gravity next step, which is right.
    if (after < REST_SPEED_MPS && n.y > 0.5) {
      s.vel.x = 0;
      s.vel.y = 0;
      s.vel.z = 0;
      s.atRest = true;
      break;
    }
  }

  if (s.pos.y < MAX_FALL_Y) s.atRest = true;
  return s;
}

/** Where a throw leaves from, given the eye and a unit look direction. */
export function throwOrigin(eye: Vec3, dir: Vec3): Vec3 {
  return {
    x: eye.x + dir.x * THROW_ORIGIN_FWD_M,
    y: eye.y + dir.y * THROW_ORIGIN_FWD_M - THROW_ORIGIN_DOWN_M,
    z: eye.z + dir.z * THROW_ORIGIN_FWD_M,
  };
}

/**
 * The launch velocity for a look direction: pitched up a little so a horizon
 * throw still arcs, scaled to `THROW_SPEED_MPS`, plus the thrower's velocity.
 */
export function throwVelocity(dir: Vec3, throwerVel: Vec3 | null): Vec3 {
  // Rotate `dir` upward about the horizontal axis perpendicular to it.
  const horiz = Math.sqrt(dir.x * dir.x + dir.z * dir.z);
  const pitch = Math.atan2(dir.y, horiz) + THROW_PITCH_UP_RAD;
  const cp = Math.cos(pitch);
  const sp = Math.sin(pitch);
  const hx = horiz > 1e-6 ? dir.x / horiz : 0;
  const hz = horiz > 1e-6 ? dir.z / horiz : 1;
  return {
    x: hx * cp * THROW_SPEED_MPS + (throwerVel?.x ?? 0),
    y: sp * THROW_SPEED_MPS + (throwerVel?.y ?? 0),
    z: hz * cp * THROW_SPEED_MPS + (throwerVel?.z ?? 0),
  };
}

export interface BlastTarget<T> {
  target: T;
  /** 1 at the centre, 0 at the radius. Linear — a blast is not a bullet. */
  mult: number;
  dist: number;
}

/**
 * Who a blast reaches: each pose within `radiusM` whose chest has clear line to
 * the centre — a wall is a wall, no partial cover. `chestOf` maps a pose to the
 * tested point.
 */
export function blastTargets<T>(
  center: Vec3,
  radiusM: number,
  world: CollisionWorld,
  candidates: Iterable<T>,
  poseOf: (t: T) => HitPose,
  chestOf: (p: HitPose) => Vec3,
): BlastTarget<T>[] {
  const out: BlastTarget<T>[] = [];
  const r2 = radiusM * radiusM;
  for (const t of candidates) {
    const chest = chestOf(poseOf(t));
    const dx = chest.x - center.x;
    const dy = chest.y - center.y;
    const dz = chest.z - center.z;
    const d2 = dx * dx + dy * dy + dz * dz;
    if (d2 > r2) continue;
    const dist = Math.sqrt(d2);
    if (dist > 1e-6) {
      const dir = { x: dx / dist, y: dy / dist, z: dz / dist };
      if (world.raycast(center, dir, dist) !== null) continue;
    }
    out.push({ target: t, mult: 1 - dist / radiusM, dist });
  }
  return out;
}
